import { createFileRoute, Link } from "@tanstack/react-router";
import { getArticles, getCategories } from "@/lib/sanity.server";
import BlogCard from "@/components/blog/BlogCard";
import Pagination from "@/components/blog/Pagination";
import CTA from "@/components/LandingPage/CTA";
import { createLLMOMeta, createBreadcrumbSchema } from "@/lib/seo";

const parsePositivePage = (value: unknown) => {
  const page = typeof value === "number" ? value : typeof value === "string" ? Number(value) : 1;
  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
};

export const Route = createFileRoute("/blog_/author/$slug")({
  validateSearch: (search) => ({
    page: parsePositivePage(search.page),
  }),
  loaderDeps: ({ search: { page } }) => ({ page }),
  loader: async ({ params, deps: { page } }) => {
    // We use the author slug to filter — the getArticles server fn doesn't support
    // author filter natively yet, so we fetch all and note this is a placeholder.
    // For now we pass it as a search query matching author name.
    const { articles, pagination } = await getArticles({
      data: { page, pageSize: 9 },
    });

    // Filter client-side by author slug (until we add server-side author filter)
    const filtered = articles.filter(
      (a) => a.author?.slug === params.slug || a.author?.name?.toLowerCase().replace(/\s+/g, "-") === params.slug
    );

    return {
      articles: filtered,
      pagination: { ...pagination, total: filtered.length, pageCount: 1 },
      authorSlug: params.slug,
      authorName: filtered[0]?.author?.name || params.slug.replace(/-/g, " "),
    };
  },
  head: ({ loaderData }) => {
    const name = loaderData?.authorName || "Author";
    const title = `Posts by ${name} | Zapify Blog`;
    const url = `https://zapify.pro/blog/author/${loaderData?.authorSlug}`;

    return {
      meta: [
        ...createLLMOMeta({
          title,
          description: `Read all blog posts written by ${name} on Zapify Blog.`,
          url,
          author: name,
        }),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        createBreadcrumbSchema({
          itemListElement: [
            { position: 1, name: "Home", item: "https://zapify.pro" },
            { position: 2, name: "Blog", item: "https://zapify.pro/blog" },
            { position: 3, name: name, item: url },
          ],
        }),
      ],
    };
  },
  component: AuthorPage,
});

function AuthorPage() {
  const { articles, pagination, authorName, authorSlug } = Route.useLoaderData();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 sm:pt-36 pb-16">
        {/* Breadcrumbs */}
        <nav className="mb-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary transition-colors">Home</Link>
          <span>/</span>
          <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
          <span>/</span>
          <span className="text-foreground capitalize">{authorName}</span>
        </nav>

        {/* Author Header */}
        <div className="mb-12 text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary-gradient flex items-center justify-center text-primary-foreground font-bold text-2xl">
            {authorName.charAt(0).toUpperCase()}
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-foreground mb-2 capitalize">
            {authorName}
          </h1>
          <p className="text-muted-foreground">
            {articles.length} {articles.length === 1 ? "post" : "posts"}
          </p>
        </div>

        {/* Posts Grid */}
        {articles.length > 0 ? (
          <>
            <h2 className="mb-8 text-2xl font-bold text-foreground">Posts by {authorName}</h2>
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {articles.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
            <div className="mt-12">
              <Pagination
                currentPage={pagination.page}
                pageCount={pagination.pageCount}
                pageSize={pagination.pageSize}
                total={pagination.total}
              />
            </div>
          </>
        ) : (
          <div className="py-16 text-center">
            <p className="text-lg text-muted-foreground">No posts by this author yet.</p>
            <Link
              to="/blog"
              className="mt-4 inline-flex text-primary hover:underline font-medium"
            >
              ← Back to Blog
            </Link>
          </div>
        )}
      </div>
      <CTA />
    </div>
  );
}
